<template>
  <div class="modal-wrapper">
    <modal-header title="Generative AI news summary" :on-modal-close="close" />
    <div class="homebase-modal-body pb-2 px-5">
      <div v-if="isLoading" class="pt-4">
        <div class="generating-msg">Generating...</div>
        <content-loader
          :width="300"
          :height="60"
          preserveAspectRatio="none"
          class="mb-1 search-loading-component"
        >
          <rect x="0" y="10" rx="4" ry="4" width="300" height="6" />
          <rect x="0" y="30" rx="4" ry="4" width="300" height="6" />
        </content-loader>
      </div>
      <div v-else>
        <div class="row">
          <div class="col-12 pb-3">
            <div class="alert alert-error mt-3" v-if="isErrored">
              {{ CONSTANTS.SERVER_ERROR_MESSAGE }}
            </div>
            <div v-else class="pt-4 pb-2">
              <div class="d-flex flex-column-reverse flex-sm-row justify-content-sm-between align-items-sm-end">
                <div class="content-title pb-2">Top stories at Autodesk</div>
                <div v-if="isFullContentLoaded" class="pb-3">
                  <div class="share-text d-inline-block mr-1"><strong>{{shareText}}</strong></div>
                  <div class="share-icon d-inline-block"> 
                    <a class="d-flex w-100 h-100 justify-content-center align-items-center" @click="handleMailToClick" :href="mailToLink">
                      <font-awesome-icon :icon="['fas', 'envelope']" />
                    </a>
                  </div>
                  <div class="share-icon d-inline-block">
                    <a class="d-flex w-100 h-100 justify-content-center align-items-center" @click="handleCopyLink">
                      <font-awesome-icon :icon="['fas', 'link']" />
                    </a>
                  </div>
                </div>
              </div>
              <div class="news-summary">
                <div
                  v-if="isFullContentLoaded"
                  @click="handleContentClick($event)"
                >
                  <div v-html="newsSummary"></div>
                </div>
                <div
                  v-else
                  class="typed-container"
                  @click="handleContentClick($event)"
                >
                  <div class="static-content">
                    <div v-html="newsSummary" v-show="startTyping"></div>
                  </div>
                  <div class="typed-content">
                    <div id="typed-strings">
                      <div v-html="newsSummary" v-show="startTyping"></div>
                    </div>
                    <span id="typed"></span>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="feedback-wrapper d-flex justify-content-end"
              @click="handleFeedbackClick()"
            >
              <font-awesome-icon :icon="['far', 'comments']" size="lg" />
              <strong class="ml-2">Feedback</strong>
            </div>
            <div class="tooltip-wrapper d-flex justify-content-end">
              <div v-if="showTooltip" class="news-summary-tooltip">
                Generative AI summarizes Autodesk's top stories using artificial
                intelligence. This experimental experience may provide
                inaccurate or unexpected results. Your use of and feedback on
                generative AI summaries will help improve the experience.
              </div>
              <div
                class="tooltip-text pt-1"
                @mouseover="toggleTooltip"
                @mouseleave="toggleTooltip"
              >
                What is a generative AI news summary?
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import api from "@/api";
import * as CONSTANTS from "@/store/constants";
import { mapGetters } from "vuex";

// package imports
import { API, graphqlOperation } from "@aws-amplify/api";
import { Amplify } from "@aws-amplify/core";
import Typed from "typed.js";

// modal elements
import ModalHeader from "../modals/ModalHeader";
import ModalLoading from "../modals/ModalLoading";

import HomebaseMixpanelMixin from "@/mixins/HomebaseMixpanelMixin";

import { v4 as uuidv4 } from "uuid";
import { ContentLoader } from "vue-content-loader";

import QualtricsMixin from "@/mixins/QualtricsMixin.js";

export default {
  name: "summarize-news-modal",
  components: {
    ModalHeader,
    ModalLoading,
    ContentLoader,
  },
  mixins: [HomebaseMixpanelMixin, QualtricsMixin],
  data() {
    return {
      CONSTANTS,
      isLoading: true,
      isErrored: false,
      newsSummary: "",
      requestId: "",
      appSyncSubscriber: null,
      typed: null,
      startTyping: false,
      isFullContentLoaded: false,
      showTooltip: false,
      currOrder: 0,
      chunkBuffer: {},
      shareText: "Share"
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    ...mapGetters(["user", "authResponse"]),
    mailToLink() {
      let bodyText = encodeURIComponent(CONSTANTS.NEWS_SUMMARY_EMAIL_BODY);
      return `mailto:?subject=${CONSTANTS.NEWS_SUMMARY_EMAIL_SUBJECT}&body=${bodyText}`;
    }
  },
  methods: {
    getSubscriptionQuery(requestId) {
      return `subscription MySubscription {
              onSummarizeNews(userRequestId: "${requestId}") {
                news
                order
              }
            }`;
    },

    initialize() {
      this.isLoading = true;
      let requestId = uuidv4();
      this.getNewsSummaryStream(requestId);
      this.getNewsSummary(requestId);
      this.callSurvey();
    },

    async getNewsSummary(requestId) {
      const minimumDuration = 2000;
      const startTime = Date.now();

      try {
        let res = await api.getNewsSummary(requestId);

        if (res.data && res.data.errors && res.data.errors.length > 0) {
          if (res.data.errors[0].errorType !== CONSTANTS.LAMBDA_TIMEOUT_ERROR) {
            this.isErrored = true;
            if (this.appSyncSubscriber) this.appSyncSubscriber.unsubscribe();
          }
        } else {
          this.newsSummary = res.data.data.summarizeNews;
          this.isFullContentLoaded = true;
          if (this.appSyncSubscriber) this.appSyncSubscriber.unsubscribe();
        }
      } catch (e) {
        // caught error
      } finally {
        // enforce 2 second load time for cached response
        const duration = minimumDuration - (Date.now() - startTime);
        setTimeout(() => (this.isLoading = false), Math.max(duration, 0));
      }
    },

    async getNewsSummaryStream(requestId) {
      try {
        const apiEndPoint = AONE_GEN_AI_BASE_URL + "/graphql";
        Amplify.configure({
          aws_appsync_graphqlEndpoint: apiEndPoint,
          aws_appsync_region: "us-east-1",
          aws_appsync_authenticationType: "AWS_LAMBDA",
        });

        this.appSyncSubscriber = API.graphql({
          ...graphqlOperation(this.getSubscriptionQuery(requestId)),
          authMode: "AWS_LAMBDA",
          authToken: this.authResponse.accessToken,
          headers: { Authorization: this.authResponse.accessToken },
        }).subscribe({
          next: (msg) => {
            if (
              msg &&
              msg.value &&
              msg.value.data &&
              msg.value.data.onSummarizeNews &&
              msg.value.data.onSummarizeNews.news &&
              Object.hasOwn(msg.value.data.onSummarizeNews, 'order')
            ) {
              this.isLoading = false;

              let currRes = msg.value.data.onSummarizeNews.news;
              let order = msg.value.data.onSummarizeNews.order;

              if (currRes === CONSTANTS.NEWS_SUMMARY_COMPLETE_STATE) {
                currRes = " ";
                this.appSyncSubscriber.unsubscribe();
                this.isFullContentLoaded = true;
              }

              if (this.isFullContentLoaded) {
                this.chunkBuffer = {};
                this.currOrder = 0;
              } else {
                if (order === this.currOrder) {
                  ++this.currOrder;
                  this.newsSummary += currRes;
                  while (Object.hasOwn(this.chunkBuffer, this.currOrder)) {
                    this.newsSummary += this.chunkBuffer[this.currOrder];
                    ++this.currOrder;
                  }
                  this.triggerTyping();
                } else {
                  this.chunkBuffer[order] = currRes;
                }
              }
            }
          },
          error: (e) => {
            this.isErrored = true;
            if (this.appSyncSubscriber) this.appSyncSubscriber.unsubscribe();
            this.isLoading = false;
          },
        });
      } catch (e) {
        // caught response
        this.isErrored = true;
        if (this.appSyncSubscriber) this.appSyncSubscriber.unsubscribe();
        this.isLoading = false;
      }
    },

    close() {
      this.$emit("update:modelValue", false);
    },

    triggerTyping() {
      setTimeout(() => {
        this.typed = new Typed("#typed", {
          stringsElement: "#typed-strings",
          showCursor: false,
          contentType: "html",
          typeSpeed: -1,
          onBegin: () => (this.startTyping = true),
        });
      }, 200);
    },

    handleContentClick(event) {
      if (event && event.target && event.target.href) {
        this.trackGenAiNewsReadMoreClicked(event.target.href);
      }
    },

    toggleTooltip() {
      this.showTooltip = !this.showTooltip;
    },

    handleFeedbackClick() {
      this.showFeedbackModal(true);
    },

    handleMailToClick() {
      this.updateShareText("Generating e-mail...")
      this.trackGenAiShareNewsSummaryClicked("via e-mail");
    },

    handleCopyLink() {
      let copyText = window.location.href;
      navigator.clipboard.writeText(copyText);
      this.updateShareText("Link copied!")
      this.trackGenAiShareNewsSummaryClicked("link copied");
    },

    updateShareText(newText) {
      this.shareText = newText;
      setTimeout(() => {
        if (this.shareText === newText) this.shareText = "Share";
      }, 5000)
    }
  },

  beforeUnmount() {
    if (this.typed) this.typed.destroy();
    if (this.appSyncSubscriber) this.appSyncSubscriber.unsubscribe();
  },
};
</script>

<style scoped>
.generating-msg,
.content-title {
  font-family: artifaktElementBold !important;
}
.news-summary :deep(li) {
  padding-bottom: 10px;
}
.news-summary :deep(ul) {
  padding-inline-start: 20px;
  margin-block-end: 0 !important;
}
.news-summary :deep(b),
.news-summary :deep(strong) {
  font-family: artifaktElementBold !important;
  font-weight: normal !important;
}
/* overlay styles */
.typed-container {
  position: relative;
}
.static-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  color: rgba(0, 0, 0, 0) !important;
}
.static-content :deep(*) {
  color: rgba(0, 0, 0, 0) !important;
}
.typed-content {
  z-index: 2;
}
.feedback-icon {
  width: 35px !important;
  cursor: pointer;
}
.thumbs-down {
  transform: rotate(180deg);
}
.tooltip-wrapper {
  position: relative;
}
.share-text {
  font-size: 14px;
}
.tooltip-text {
  font-size: 12px;
}
.tooltip-text:hover {
  cursor: arrow;
}
.news-summary-tooltip {
  font-size: 14px;
  background: black;
  border-radius: 4px;
  color: white;
  padding: 10px 15px;
  position: absolute;
  bottom: 20px;
  width: 50%;
}
.feedback-wrapper:hover {
  cursor: pointer;
}
.share-icon {
  background: black;
  width: 36px;
  height: 36px;
  border-radius: 100%;
  margin-left: 5px;
}
.share-icon:hover {
  cursor: pointer;
}
.share-icon svg {
  color: white;
  vertical-align: middle !important;
}
</style>
